import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The LIBRARY drag on both User Settings tabs: an anchor picked up in the middle
 * column (`.right-column-scrollable`) and carried to the format rail.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★ WHY THIS IS A POINTER DRAG AND NOT NATIVE HTML5 DnD.
 *
 * Legacy drives it with jQuery-UI (`submittal.js:862-878`, `proposal.js:638-654`):
 *
 *     widget.draggable({
 *       connectToSortable: "#…-format-list-wrapper-row",
 *       stack: ".…item-for-drag",
 *       revert: true, revertDuration: 200,
 *       helper: "clone",
 *       start: … self.dropping = true; ui.helper.css("z-index", 9999) …,
 *       stop:  … self.dropping = false …,
 *     });
 *
 * That is a MOUSE drag of a **cloned DOM node**, and three things follow from it
 * that native DnD cannot give you — all three were reported against the React
 * page (2026-09-09):
 *
 *   1. **You can see what you are dragging.** `helper: "clone"` puts a real copy
 *      of the row in the page, under the cursor, styled by the same CSS as the
 *      original. Native DnD hands the OS a drag image instead: it is not part of
 *      the page, it does not pick up the app's styling, and it does not even
 *      appear in a screen recording — which is exactly how the two Looms differ.
 *   2. **There is no drop target other than the sortable.** The browser is not
 *      involved, so a text input cannot accept the drag and a link drag cannot
 *      navigate the page. Let go anywhere else and `revert: true` animates the
 *      clone home over 200 ms.
 *   3. **The rail's gap is continuous.** The placeholder is driven by mousemove,
 *      not by `dragenter`/`dragleave`, so it opens once when the pointer reaches
 *      the rail and then tracks it. The native port's gap blinked shut every time
 *      the pointer crossed from a row to the container.
 *
 * So the transport is jQuery-UI's, reproduced: `mousedown` on the anchor arms it,
 * `$.ui.mouse`'s `distance: 1` starts it, a clone follows the pointer in BOTH
 * axes (a draggable has no `axis` option set here — unlike the rail's sortable),
 * and `mouseup` either drops into the rail or reverts.
 *
 * ★★ `connectToSortable` IS A HANDOVER, NOT A DROP TARGET — and that is what
 * stops a library item being dragged away to the right (reported 2026-09-14).
 * The moment the clone intersects the rail, jQuery-UI appends it INTO the
 * sortable and lets the sortable position it from then on. The rail's sortable
 * is `axis: "y"`, so the clone is pinned to the X it arrived at and will not
 * move sideways again however far right the pointer goes — see `overLeft`.
 * Reading the plugin as "the sortable is where a drop is accepted" misses this
 * entirely: it also owns the clone's movement for the rest of the drag.
 *
 * ★ The INTERNAL reorder is a different widget and stays where it is
 * (`useRailSortable`): `sortable({axis: "y"})`, lifted row, Y-only travel,
 * containment. Both are pointer drags now, which is what legacy is, so the two
 * halves can no longer disagree about what a drag is.
 *
 * ★ The anchors keep `draggable="true"` — it is in the legacy markup — but the
 * native drag is cancelled at `dragstart` (see `anchorDragProps` in the two
 * libraries). jQuery-UI gets the same result by `preventDefault`ing the
 * `mousedown`; done at `dragstart` here so the anchor still takes focus.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** `$.ui.mouse`'s `distance` option: a press that never travels a pixel starts
 * no drag, so a plain click on a library row is still a click. */
const DRAG_DISTANCE = 1;
/** `revertDuration: 200` — the clone's animation home when the drop is refused. */
const REVERT_DURATION = 200;
/** jQuery-UI's `cancel` default: a press on the row's trash never starts a drag. */
const CANCEL_SELECTOR = 'input,textarea,button,select,option';
/** `$.ui.sortable`'s `scrollSensitivity` / `scrollSpeed`, as in `useRailSortable`:
 * dragging at either edge of the rail pages it, so a rail taller than its
 * viewport can still be reached. */
const SCROLL_SENSITIVITY = 20;
const SCROLL_SPEED = 20;

/**
 * ★ jQuery-UI draggable's `iframeFix` — "prevent iframes from capturing the
 * mousemove events during a drag".
 *
 * A mouse drag is only a drag because the whole document keeps receiving
 * `mousemove`/`mouseup` while the button is down. An `<iframe>` breaks that: it
 * is a separate document, so once the pointer crosses into one, ITS document
 * gets the events and ours gets nothing — no move, and critically no `mouseup`.
 * The drag then never ends: the helper freezes at the last position we saw and
 * stays in the page for good, which is not a drop the rail refused, it is a drop
 * that never happened.
 *
 * The Submittal tab is full of them: `UserFieldPane:197` renders the PDF preview
 * as a pane-sized `<iframe>` (legacy `submittal.js:404-409`), so carrying a
 * library field to the right of the rail parks the clone on top of it
 * permanently — reported 2026-09-14.
 *
 * Legacy never hits this even with `iframeFix` at its default `false`, because
 * jQuery-UI's helper has no `pointer-events` override: the clone tracks the
 * cursor exactly, so the cursor sits on the HELPER — an element in our own
 * document — and never reaches the iframe beneath it. Our helper is
 * `pointer-events: none`, which removes that accidental shield, so put
 * jQuery-UI's explicit one back.
 *
 * Same construction as `$.ui.draggable._mouseStart`: a transparent div over
 * every iframe for the life of the drag, removed in `_mouseStop`.
 */
/**
 * `$.ui.draggable._convertPositionTo("relative")`.
 *
 * The helper is `position: absolute` (jQuery-UI's `_createHelper` forces it),
 * so its `left`/`top` live in its offsetParent's padding-box space — and that
 * offsetParent CHANGES mid-drag, because the sortable re-parents the clone into
 * the rail when it takes over. So every placement converts from the viewport
 * coordinates the drag reasons in, against whatever the offsetParent is now.
 */
const placeHelper = (node: HTMLElement, viewportLeft: number, viewportTop: number) => {
  const parent = node.offsetParent as HTMLElement | null;
  let left = viewportLeft;
  let top = viewportTop;
  if (parent) {
    const box = parent.getBoundingClientRect();
    const style = window.getComputedStyle(parent);
    // `getBoundingClientRect` is the BORDER box and offsets are measured from
    // the PADDING box, hence the border widths; `scrollLeft/Top` because
    // offsets ignore the offsetParent's own scrolling.
    left += parent.scrollLeft - box.left - parseFloat(style.borderLeftWidth || '0');
    top += parent.scrollTop - box.top - parseFloat(style.borderTopWidth || '0');
  }
  node.style.left = `${left}px`;
  node.style.top = `${top}px`;
};

const shieldIframes = (): (() => void) => {
  const shields = Array.from(document.querySelectorAll('iframe')).map((frame) => {
    const box = frame.getBoundingClientRect();
    const shield = document.createElement('div');
    shield.className = 'ui-draggable-iframeFix';
    shield.style.cssText = [
      // `position: fixed` where jQuery-UI uses absolute + `$(this).offset()`.
      // The shield is appended to `body` and measured from a viewport rect, so
      // fixed keeps the two in the same coordinate space and no scrolling
      // ancestor can slide it off the iframe it covers. (The HELPER is
      // `absolute`, as jQuery-UI has it — these are different elements with
      // different jobs.)
      'position:fixed',
      `left:${box.left}px`,
      `top:${box.top}px`,
      `width:${box.width}px`,
      `height:${box.height}px`,
      'background:#fff',
      'opacity:0.001',
      // Under the helper's 9999 — it only has to beat the iframe.
      'z-index:1000',
    ].join(';');
    document.body.appendChild(shield);
    return shield;
  });
  return () => shields.forEach((shield) => shield.remove());
};

interface UseLibraryDragOptions<T> {
  /** The sortable container — `#…-format-list-wrapper-row`, the ONE place a
   * library item may land (`connectToSortable`). */
  railRef: React.MutableRefObject<HTMLDivElement | null>;
  /** The rail's scroll wrapper — the auto-scroll target. */
  scrollerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Legacy's `self.dropping`, with the dropped item's attributes attached. Set
   * on `start`, cleared on `stop` — and, as in legacy, cleared only AFTER the
   * drop handler has run, because that handler branches on it. */
  dragPayloadRef: React.MutableRefObject<T | null>;
  /** The sortable's `stop` with `self.dropping` true → `createNewField` at
   * `new_index` (§6 W8). Called only for a drop that landed on the rail. */
  onDrop: (index: number) => void;
}

export interface LibraryDrag<T> {
  /** Where the rail should open its gap, or `null` when no library drag is over
   * it — jQuery-UI's `ui-sortable-placeholder`. */
  placeholderIndex: number | null;
  /** Bind to every draggable library anchor's `onMouseDown`. */
  onAnchorMouseDown: (e: React.MouseEvent<HTMLAnchorElement>, payload: T) => void;
}

export const useLibraryDrag = <T,>({
  railRef,
  scrollerRef,
  dragPayloadRef,
  onDrop,
}: UseLibraryDragOptions<T>): LibraryDrag<T> => {
  const [placeholderIndex, setPlaceholderIndex] = useState<number | null>(null);
  /** Refreshed every render so the document listeners never read a stale
   * callback if the drag outlives a re-render. */
  const latest = useRef({ onDrop });
  latest.current = { onDrop };
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => () => releaseRef.current?.(), []);

  const onAnchorMouseDown = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, payload: T) => {
      // `$.ui.mouse._mouseDown`: left button only, and never on a `cancel`ed
      // control (the per-row trash button).
      if (e.button !== 0) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest(CANCEL_SELECTOR)) return;

      const anchor = e.currentTarget;
      const startX = e.clientX;
      const startY = e.clientY;
      let started = false;
      let helper: HTMLElement | null = null;
      /** `iframeFix`'s teardown, armed at `_mouseStart` with the helper. */
      let unshieldIframes: (() => void) | null = null;
      /** `draggable._parent` (`jquery-ui:10642`) — the clone's home, stored so
       * the sortable can hand it back. `appendTo: "parent"`'s target. */
      const home = anchor.parentElement;
      /** The anchor's box at lift-off — the helper's origin, and where a
       * refused drop reverts to. */
      let origin = { left: 0, top: 0, width: 0, height: 0 };
      /**
       * ★ jQuery-UI's `refreshPositions` cache, for the same reason
       * `FormatRail` cached it before: inserting the placeholder pushes every
       * row below it down one slot, so re-measuring live rects feeds the
       * shifted geometry back into the midline test and the gap settles one
       * slot high. Measured once, when the pointer first reaches the rail.
       */
      let rowMids: number[] | null = null;
      /**
       * ★ `$.ui.sortable.isOver` — whether the rail's sortable has TAKEN OVER
       * the helper (§ `overLeft`). The drop at `mouseup` is decided by this and
       * nothing else, exactly as `connectToSortable.stop` reads
       * `if (sortable.isOver)`; the pointer is not re-tested there.
       */
      let isOver = false;
      /** Where the placeholder currently sits — the gap `stop` reads back as
       * `new_index` off `ui.item.next(...)` (`submittal.js:847-849`). */
      let overIndex: number | null = null;
      /**
       * ★★ THE HELPER'S FROZEN X — `sortable.originalPosition.left` under
       * `axis: "y"`, and the whole reason a library item cannot be dragged off
       * to the right.
       *
       * `connectToSortable` is not a drop target, it is a HANDOVER. The moment
       * the helper intersects the rail, jQuery-UI appends it INTO the sortable
       * and makes the sortable drive it (`draggable.js:10644-10659`): the
       * sortable's `_mouseDrag`/`_generatePosition` position the clone from then
       * on, and `ui.position = sortable.position` (`:10693`) overwrites whatever
       * the draggable computed. The rail's sortable is
       * `sortable({items: ".submittal-item-for-drag", axis: "y"})`
       * (`submittal.js:841-843`), and `axis: "y"` makes `_generatePosition`
       * return `this.originalPosition.left` verbatim — so once the rail has the
       * helper, **it travels vertically and cannot move sideways at all**, no
       * matter how far right the pointer goes.
       *
       * `null` while the draggable still owns it (free in both axes, no `axis`
       * on the draggable itself), a latched viewport X once the rail does.
       * Cleared on hand-back, where `_refreshOffsets` + `_generatePosition`
       * (`:10725-10726`) snap the clone back under the cursor.
       */
      let overLeft: number | null = null;

      /** The drop index: the first row whose midline the pointer has not
       * reached, or the end of the list. */
      const indexFor = (clientY: number, rail: HTMLElement) => {
        if (!rowMids) {
          rowMids = Array.from(rail.querySelectorAll<HTMLElement>(':scope > [data-index]')).map(
            (el) => {
              const b = el.getBoundingClientRect();
              return b.top + b.height / 2;
            }
          );
        }
        const i = rowMids.findIndex((m) => clientY < m);
        return i === -1 ? rowMids.length : i;
      };

      const onMove = (ev: MouseEvent) => {
        if (!started) {
          if (
            Math.max(Math.abs(ev.clientX - startX), Math.abs(ev.clientY - startY)) < DRAG_DISTANCE
          ) {
            return;
          }
          const rect = anchor.getBoundingClientRect();
          origin = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
          // `helper: "clone"` — a real copy of the row, put in the anchor's own
          // parent (`appendTo: "parent"`, the default) so every
          // ancestor-dependent rule in `user_settings/styles.css` still matches
          // it. `start` sets `z-index: 9999`.
          helper = anchor.cloneNode(true) as HTMLElement;
          helper.removeAttribute('id');
          helper.classList.add('ui-draggable-dragging');
          helper.style.cssText = [
            /**
             * ★ `position: absolute`, NOT `fixed` — `_createHelper` ends with
             * `helper.css("position", "absolute")`, and the difference is
             * visible (reported 2026-09-14: "on dev it goes under the white
             * background, on dev3 it stays visible").
             *
             * A `fixed` clone is positioned against the VIEWPORT, so no
             * ancestor's `overflow` can ever clip it and it floats over the
             * whole page. An `absolute` one resolves against a containing block
             * inside the page, so the library's own
             * `.udf-scroll { overflow-x: hidden }` (`user_settings/styles.css:
             * 2384-2388`) clips it: carry the clone right and it is cut off at
             * the list's edge rather than sailing across the detail pane.
             *
             * The port chose `fixed` to stop the clone being clipped on its way
             * to the rail — but that was solving a problem it had created by
             * not re-parenting on handover (see `overLeft`). Legacy is never
             * clipped *out of view* because by the time the clone leaves the
             * library, the rail owns it and it is clipped by the RAIL.
             */
            'position:absolute',
            `width:${rect.width}px`,
            `height:${rect.height}px`,
            'margin:0',
            'z-index:9999',
            'pointer-events:none',
          ].join(';');
          home?.appendChild(helper);
          placeHelper(helper, rect.left, rect.top);
          // `iframeFix` — measured now, with the helper, because that is when
          // the iframes are where the drag will find them.
          unshieldIframes = shieldIframes();
          // Legacy's `disableSelection` — a bare mouse drag would otherwise
          // paint a text selection across the library.
          document.body.style.userSelect = 'none';
          window.getSelection()?.removeAllRanges();
          started = true;
        }

        /**
         * `draggable.positionAbs` — where the clone WOULD be if the draggable
         * still owned it. jQuery-UI keeps computing this every frame even while
         * the sortable is driving the helper (`_mouseDrag` sets it before
         * `_trigger("drag")` runs the plugin), and it is what the intersection
         * below reads — so a helper frozen against the rail still "leaves" when
         * the pointer travels far enough back out.
         */
        const freeLeft = origin.left + (ev.clientX - startX);
        const freeTop = origin.top + (ev.clientY - startY);

        const rail = railRef.current;
        const box = rail?.getBoundingClientRect();
        /**
         * `sortable._intersectsWith(sortable.containerCache)` at the sortable's
         * default `tolerance: "intersect"`. Note what it compares: the HELPER'S
         * CENTRE against the container's box (`jquery-ui:16147-16150`, which
         * reduces to `l < centreX < r && t < centreY < b`) — **not** the pointer.
         * A library row is wider than the rail, so the handover fires once the
         * clone is roughly half way across, before the pointer itself arrives.
         */
        const over =
          box !== undefined &&
          freeLeft + origin.width / 2 > box.left &&
          freeLeft + origin.width / 2 < box.right &&
          freeTop + origin.height / 2 > box.top &&
          freeTop + origin.height / 2 < box.bottom;

        // ── The handover, both ways ──────────────────────────────────────
        if (over && !isOver) {
          // Latching the X reproduces `sortable.originalPosition.left`: the X
          // the clone had at the instant the rail took it, held for as long as
          // the rail keeps it.
          overLeft = freeLeft;
          // `sortable.currentItem = ui.helper.appendTo(sortable.element)`
          // (`jquery-ui:10644-10646`) — the clone physically MOVES INTO the
          // rail. That is what takes it out of the library's `overflow-x`
          // clip and puts it under the rail's instead.
          if (helper && rail) rail.appendChild(helper);
        } else if (!over && isOver) {
          overLeft = null;
          // `ui.helper.appendTo(draggable._parent)` (`jquery-ui:10724`) — handed
          // back, clipped by the library again, and free in both axes.
          if (helper) home?.appendChild(helper);
        }
        isOver = over;

        if (helper) placeHelper(helper, overLeft ?? freeLeft, freeTop);

        if (!rail || !over) {
          // Left the sortable: it drops its placeholder and forgets its
          // geometry, so the next entry re-measures an unshifted list.
          rowMids = null;
          overIndex = null;
          setPlaceholderIndex(null);
          return;
        }

        // `scroll: true` — page the rail when the pointer nears either edge.
        const scroller = scrollerRef.current;
        if (scroller) {
          const sbox = scroller.getBoundingClientRect();
          if (ev.clientY - sbox.top < SCROLL_SENSITIVITY) {
            scroller.scrollTop -= SCROLL_SPEED;
          } else if (sbox.bottom - ev.clientY < SCROLL_SENSITIVITY) {
            scroller.scrollTop += SCROLL_SPEED;
          }
        }
        overIndex = indexFor(ev.clientY, rail);
        setPlaceholderIndex(overIndex);
      };

      const release = () => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
        if (started) document.body.style.userSelect = '';
        // `_mouseStop`'s `$("div.ui-draggable-iframeFix").remove()`. In
        // `release` rather than `onUp` so an unmount mid-drag takes them too.
        unshieldIframes?.();
        unshieldIframes = null;
        releaseRef.current = null;
      };

      /** `revert: true` / `revertDuration: 200` — the clone animates back to the
       * row it came from and is then removed. Every drop that did not land on
       * the sortable ends here, which is the whole of "you cannot drop it
       * anywhere else": there is nothing to drop ON. */
      const revert = (node: HTMLElement) => {
        node.style.transition = `left ${REVERT_DURATION}ms, top ${REVERT_DURATION}ms`;
        // A refused drop is by definition one the rail did not have, so the
        // clone is already back in `home` and this resolves against it.
        placeHelper(node, origin.left, origin.top);
        window.setTimeout(() => node.remove(), REVERT_DURATION);
      };

      const onUp = () => {
        release();
        if (!started) {
          dragPayloadRef.current = null;
          return;
        }
        // `preventClickEvent`: jQuery-UI's mouse widget swallows the click that
        // trails a real drag, so the row the drag started on is not also
        // selected. One-shot, capture, so nothing downstream sees it.
        const swallowClick = (click: MouseEvent) => {
          click.preventDefault();
          click.stopImmediatePropagation();
          document.removeEventListener('click', swallowClick, true);
        };
        document.addEventListener('click', swallowClick, true);
        window.setTimeout(() => document.removeEventListener('click', swallowClick, true), 0);

        // ★ `connectToSortable.stop` (`jquery-ui:10572`) branches on
        // `sortable.isOver` and re-tests NOTHING: the drop is decided by the
        // handover state the last `drag` left behind, and the index by where the
        // placeholder already sits. A `mouseup` cannot move either.
        const index = isOver ? overIndex : null;
        setPlaceholderIndex(null);

        if (index === null) {
          if (helper) revert(helper);
          dragPayloadRef.current = null;
          return;
        }
        // Landed on the sortable: the row is about to be rendered for real, so
        // the clone goes immediately rather than animating home.
        helper?.remove();
        latest.current.onDrop(index);
        // ★ Legacy clears `self.dropping` in the DRAGGABLE's `stop`, which fires
        // after the sortable's — so the create path still sees it set. Same
        // ordering here: the drop handler above has already run.
        dragPayloadRef.current = null;
      };

      dragPayloadRef.current = payload;
      releaseRef.current?.();
      releaseRef.current = release;
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
    },
    [railRef, scrollerRef, dragPayloadRef]
  );

  return { placeholderIndex, onAnchorMouseDown };
};
